
import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  IconButton,
  Tooltip
} from "@mui/material";
import { getAllVideos, createVideo, updateVideo, deleteVideo } from "../../../services/reporterService";
import { getCategories, getSubcategories } from "../../../services/apiService";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const VideoManagementbyReporter = () => {
  const [videos, setVideos] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({ title: "", videoUrl: "", category: "", subCategory: "", videoFile: null });
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  // Modal for playing video
  const [openPlayer, setOpenPlayer] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");

  const fetchVideos = async () => {
    try {
      const data = await getAllVideos();
      setVideos(data.data);
    } catch (error) {
      console.error("Error fetching videos:", error);
    }
  };

  // Load categories
  useEffect(() => {
    fetchVideos();
    const loadCategories = async () => {
      try {
        const res = await getCategories();
        setCategories(res.data);
      } catch (error) {
        console.error("Error loading categories:", error);
      }
    };
    loadCategories();
  }, []);

  // Load subcategories whenever category changes
  useEffect(() => {
    if (!formData.category) {
      setSubcategories([]);
      return;
    }
    const loadSubcategories = async () => {
      try {
        const res = await getSubcategories(formData.category);
        setSubcategories(res.data);
      } catch (error) {
        console.error("Error loading subcategories:", error);
        setSubcategories([]);
      }
    };
    loadSubcategories();
  }, [formData.category]);

  const handleOpenDialog = (video = null) => {
    setEditingVideo(video);
    setFormData(video ? { ...video, videoFile: null } : { title: "", videoUrl: "", category: "", subCategory: "", videoFile: null });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingVideo(null);
    setFormData({ title: "", videoUrl: "", category: "", subCategory: "", videoFile: null });
    setSubcategories([]);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.category) {
      alert("Please fill all required fields!");
      return;
    }

    if (!formData.videoFile && !formData.videoUrl && !editingVideo) {
      alert("Please provide a video URL or upload a file!");
      return;
    }

    try {
      const data = new FormData();
      data.append("title", formData.title);
      data.append("category", formData.category);
      if (formData.subCategory) data.append("subCategory", formData.subCategory);

      if (formData.videoFile) {
        data.append("videoFile", formData.videoFile); // file upload
      } else if (formData.videoUrl) {
        data.append("videoUrl", formData.videoUrl); // URL
      }

      if (editingVideo) {
        await updateVideo(editingVideo._id, data);
      } else {
        await createVideo(data);
      }

      fetchVideos();
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving video:", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteVideo(id);
      fetchVideos();
    } catch (error) {
      console.error("Error deleting video:", error);
    }
  };

  const getYouTubeId = (url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const handlePlayVideo = (url) => {
    setCurrentVideoUrl(url);
    setOpenPlayer(true);
  };

  const handleClosePlayer = () => {
    setCurrentVideoUrl("");
    setOpenPlayer(false);
  };

  return (
    <Paper style={{ padding: "1rem" }}>
      <Button variant="contained" color="primary" onClick={() => handleOpenDialog()}>
        Add Video
      </Button>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
                  <TableCell>#</TableCell>
              <TableCell>Video</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>status</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>SubCategory</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {videos.map((video,idx) => (
              <TableRow key={video._id}>
                <TableCell>{idx + 1}</TableCell>
                <TableCell>
                  {video.videoUrl.includes("youtube.com") || video.videoUrl.includes("youtu.be") ? (
                    <img
                      src={`https://img.youtube.com/vi/${getYouTubeId(video.videoUrl)}/0.jpg`}
                      alt={video.title}
                      style={{ width: "120px", cursor: "pointer", borderRadius: ".4rem"}}
                      onClick={() => handlePlayVideo(video.videoUrl)}
                    />
                  ) : (
                    <video
                      src={video.videoUrl}
                      width="120"
                      style={{ cursor: "pointer" }}
                      onClick={() => handlePlayVideo(video.videoUrl)}
                    />
                  )}
                </TableCell>
                <TableCell>{video.title}</TableCell>
                <TableCell>{video.status}</TableCell>
                <TableCell>{video.category}</TableCell>
                <TableCell>{video.subCategory}</TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    <Tooltip title="Edit">
                      <IconButton color="primary" onClick={() => handleOpenDialog(video)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
{/* 
                    <Tooltip title="Delete">
                      <IconButton color="error" onClick={() => handleDelete(video._id)} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip> */}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Video Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editingVideo ? "Edit Video" : "Add Video"}</DialogTitle>
        <DialogContent>
  {/* Title */}
  <TextField
    label="Title"
    fullWidth
    margin="dense"
    value={formData.title}
    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
  />

  {/* Video URL */}
  <TextField
    label="Video URL"
    fullWidth
    margin="dense"
    value={formData.videoUrl}
    onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
    helperText="Either enter a URL or upload a file below"
    disabled={!!formData.videoFile} // 🔹 disable if file is selected
  />

  {/* File Upload */}
  <Box mt={2}>
    <input
      type="file"
      accept="video/*"
      onChange={(e) => setFormData({ ...formData, videoFile: e.target.files[0] })}
      disabled={!!formData.videoUrl} // 🔹 disable if URL is entered
    />
  </Box>

  {/* Category */}
  <TextField
    label="Category"
    select
    fullWidth
    margin="dense"
    value={formData.category}
    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
  >
    {categories.map(cat => (
      <MenuItem key={cat._id} value={cat._id}>{cat.name}</MenuItem>
    ))}
  </TextField>

  {/* SubCategory */}
  <TextField
    label="SubCategory"
    select
    fullWidth
    margin="dense"
    value={formData.subCategory}
    onChange={(e) => setFormData({ ...formData, subCategory: e.target.value })}
    disabled={!formData.category}
  >
    {subcategories.map(sub => (
      <MenuItem key={sub._id} value={sub._id}>{sub.name}</MenuItem>
    ))}
  </TextField>
</DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">{editingVideo ? "Update" : "Create"}</Button>
        </DialogActions>
      </Dialog>

      {/* Video Player Modal */}
      <Dialog open={openPlayer} onClose={handleClosePlayer} maxWidth="md" fullWidth>
        <DialogTitle>
          Video Player
          <IconButton
            aria-label="close"
            onClick={handleClosePlayer}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {currentVideoUrl.includes("youtube.com") || currentVideoUrl.includes("youtu.be") ? (
            <iframe
              width="100%"
              height="400px"
              src={`https://www.youtube.com/embed/${getYouTubeId(currentVideoUrl)}`}
              title="YouTube video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video src={currentVideoUrl} width="100%" height="400px" controls autoPlay />
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default VideoManagementbyReporter;















// import React from 'react';
// import { useNavigate } from 'react-router-dom';

// const VideoManagementbyReporter = () => {
//   const navigate = useNavigate();

//   // Inline Styles for the page
//   const styles = {
//     container: {
//       display: 'flex',
//       flexDirection: 'column',
//       alignItems: 'center',
//       justifyContent: 'center',
//       height: '80vh',
//       textAlign: 'center',
//       fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
//       color: '#333',
//       padding: '20px'
//     },
//     iconWrapper: {
//       fontSize: '80px',
//       marginBottom: '20px',
//       animation: 'pulse 2s infinite'
//     },
//     heading: {
//       fontSize: '2.5rem',
//       fontWeight: '700',
//       color: '#2c3e50',
//       marginBottom: '10px'
//     },
//     subHeading: {
//       fontSize: '1.2rem',
//       color: '#7f8c8d',
//       maxWidth: '600px',
//       lineHeight: '1.6'
//     },
//     badge: {
//       background: '#e67e22',
//       color: '#fff',
//       padding: '5px 15px',
//       borderRadius: '20px',
//       fontSize: '0.9rem',
//       fontWeight: 'bold',
//       textTransform: 'uppercase',
//       marginBottom: '20px',
//       display: 'inline-block'
//     },
//     button: {
//       marginTop: '30px',
//       padding: '12px 25px',
//       fontSize: '1rem',
//       backgroundColor: '#3498db',
//       color: 'white',
//       border: 'none',
//       borderRadius: '5px',
//       cursor: 'pointer',
//       transition: 'background 0.3s'
//     },
//     progressBar: {
//       width: '300px',
//       height: '10px',
//       backgroundColor: '#ecf0f1',
//       borderRadius: '5px',
//       marginTop: '30px',
//       overflow: 'hidden',
//       position: 'relative'
//     },
//     progressFill: {
//       width: '65%', // Yeh dikhayega ki 65% kaam ho gaya hai
//       height: '100%',
//       backgroundColor: '#2ecc71',
//       borderRadius: '5px',
//       animation: 'loading 2s infinite ease-in-out'
//     }
//   };

//   return (
//     <div style={styles.container}>
//       {/* Animation Style Tag */}
//       <style>
//         {`
//           @keyframes pulse {
//             0% { transform: scale(1); }
//             50% { transform: scale(1.1); opacity: 0.7; }
//             100% { transform: scale(1); }
//           }
//           @keyframes loading {
//             0% { transform: translateX(-100%); }
//             100% { transform: translateX(150%); }
//           }
//         `}
//       </style>

//       <div style={styles.iconWrapper}>🎬</div>
      
//       <div style={styles.badge}>Coming Soon / Under Development</div>
      
//       <h1 style={styles.heading}>Video Management System</h1>
      
     

//       {/* Progress Bar UI to show work in progress */}
//       <div style={styles.progressBar}>
//         <div style={styles.progressFill}></div>
//       </div>
//       <p style={{marginTop: '10px', color: '#95a5a6', fontSize: '0.8rem'}}>
//         Development Status: 65% Complete
//       </p>

//       <button 
//         style={styles.button} 
//         onClick={() => navigate(-1)} // Piche jane ke liye
//         onMouseOver={(e) => e.target.style.backgroundColor = '#2980b9'}
//         onMouseOut={(e) => e.target.style.backgroundColor = '#3498db'}
//       >
//         Go Back to Dashboard
//       </button>
//     </div>
//   );
// };

// export default VideoManagementbyReporter;